import { ApolloError } from '@apollo/client';
import { useCallback } from 'react';
import { useSalesPortalUpdateAccountMutation } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/documents/sales-portal-update-account.generated';

export interface EditAccountFormValues {
  accountId: string;
  address: string;
  address2: string;
  name: string;
  note: string;
  routeStop: string;
}

export interface EditAccountInput {
  formValues: EditAccountFormValues;
  restaurantUUID: string;
  supplierUUID: string;
}

export interface EditAccountMutatorOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export interface EditAccountMutatorResult {
  error: ApolloError | undefined;
  loading: boolean;
  updateAccount: (input: EditAccountInput) => Promise<void>;
}

/**
 * Mutator to update account details.
 *
 * @param options.onError - Callback invoked when the mutation fails
 * @param options.onSuccess - Callback invoked when the mutation succeeds
 * @returns {@link EditAccountMutatorResult}
 *
 * @example
 * ```typescript
 * const { updateAccount, loading } = useEditAccountMutator({
 *   onSuccess: () => showToast({ title: 'Account updated', variant: 'success' }),
 *   onError: (err) => showErrorToast(err.message),
 * });
 * await updateAccount({
 *   formValues: {
 *     name: 'Restaurant Name',
 *     address: '123 Main St',
 *     address2: 'Suite 100',
 *     accountId: 'ACC-001',
 *     note: 'Drop off deliveries in the back',
 *     routeStop: 'Route A',
 *   },
 *   restaurantUUID: '456',
 *   supplierUUID: '123',
 * });
 * ```
 */
export const useEditAccountMutator = (
  options?: EditAccountMutatorOptions,
): EditAccountMutatorResult => {
  const [updateAccountMutation, { loading, error }] =
    useSalesPortalUpdateAccountMutation();

  const updateAccount = useCallback<(input: EditAccountInput) => Promise<void>>(
    async (input) => {
      const { formValues, restaurantUUID, supplierUUID } = input;

      try {
        await updateAccountMutation({
          variables: {
            accountId: formValues.accountId || null,
            address: formValues.address,
            address2: formValues.address2 || null,
            name: formValues.name,
            note: formValues.note || null,
            restaurantUUID,
            routeStop: formValues.routeStop || null,
            supplierUUID,
          },
        });

        options?.onSuccess?.();
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        options?.onError?.(error);
      }
    },
    [options, updateAccountMutation],
  );

  return {
    error,
    loading,
    updateAccount,
  };
};
